// src/services/monitoring.ts
import api from './api';
import { LaporanMasalah, PpeppRekomendasi, PaginatedResponse, Periode } from '../types';

export interface MonitoringFilter {
  status?: string;
  unit_id?: number;
  risiko?: string;
  periode_id?: number;
  waktu?: string;
  search?: string;
  page?: number;
  per_page?: number;
}

export interface DashboardKPI {
  total_laporan: number;
  laporan_per_status: Record<string, number>;
  laporan_overdue: number;
  rata_rata_penyelesaian: number;
  laporan_per_risiko: Record<string, number>;
  progress_ppepp: number;
  tren_bulanan: { bulan: string; jumlah: number; selesai: number }[];
  unit_performance: { unit: string; total: number; selesai: number; overdue: number }[];
}

export interface OverdueItem {
  laporan: LaporanMasalah;
  hari_terlambat: number;
  pic: string;
  pic_unit: string;
  target_efektif?: string | null;
  sumber_target?: 'keputusan_rapim' | 'sla_awal';
}

export interface PpeppProgressResponse {
  data: PpeppRekomendasi[];
  meta: {
    current_page: number;
    per_page: number;
    total: number;
    last_page: number;
  };
  summary: {
    semua: number;
    terhambat: number;
    berlangsung: number;
    menunggu_konfirmasi: number;
    selesai: number;
    arsip: number;
  };
}

export interface ReminderData {
  laporan_id: number;
  kode: string;
  judul: string;
  target_selesai: string;
  target_efektif?: string | null;
  sumber_target?: 'keputusan_rapim' | 'sla_awal';
  sisa_hari: number;
  pic: string;
  tipe: 'h-7' | 'h-3' | 'h-1' | 'overdue';
}

export interface RiwayatPengingatItem {
  id: number;
  laporan_masalah_id: number;
  pengirim_id: number;
  penerima_id: number;
  tipe_penerima: 'pelapor' | 'pic' | 'atasan_unit';
  kanal: 'aplikasi' | 'email';
  status: 'berhasil' | 'gagal' | 'dilewati';
  pesan_tambahan: string | null;
  pesan_error: string | null;
  metadata?: Record<string, any> | null;
  dikirim_pada: string | null;
  created_at: string;
  laporan?: {
    id: number;
    kode: string;
    judul: string;
    unit?: { id: number; name: string };
  };
  pengirim?: { id: number; name: string; email: string };
  penerima?: { id: number; name: string; email: string };
}

export const monitoringService = {
  // Dashboard KPI
  // Dashboard KPI
  getDashboard: async (filters?: { periode_id?: number; unit_id?: number }) => {
    const res = await api.get('/dashboard', {
      params: {
        ...(filters || {}),
        context: 'monitoring',
      },
    });
    return res.data.data as DashboardKPI;
  },

  // Tambahkan method baru ini di dalam monitoringService
async terimaKonfirmasiSelesai(rekomendasiId: number): Promise<any> {
    const response = await api.post(
        `/monitoring/tindak-lanjut/${rekomendasiId}/terima-konfirmasi-selesai`
    );
    return response.data;
},

  // Daftar periode (untuk filter monitoring)
  getPeriodeList: async () => {
    const res = await api.get('/admin/periode');
    return (res.data?.data || []) as Periode[];
  },

  getUnitFilterOptions: async (types?: string[]) => {
    const res = await api.get('/admin/units', {
      params: types && types.length > 0 ? { type: types.join(',') } : {},
    });
    return (res.data?.data || []) as { id: number; name: string; type: string; parent_id?: number | null; path?: string | null }[];
  },

  // Dashboard per role
  getDashboardByRole: async (role: string) => {
    // Backend automatically scopes data based on logged-in user role
    const res = await api.get('/dashboard');
    return res.data.data as DashboardKPI;
  },

  // Overdue list
  getOverdue: async (filters?: MonitoringFilter) => {
    const res = await api.get('/monitoring/overdue', { params: filters });
    return res.data.data as PaginatedResponse<OverdueItem>;
  },

  // Progress PPEPP tracking
  getProgressPpepp: async (filters?: MonitoringFilter) => {
    const res = await api.get('/monitoring/ppepp-progress', { params: filters });
    return (res.data || {}) as PpeppProgressResponse;
  },

  // Tindak lanjut detail
  getTindakLanjutDetail: async (rekomendasiId: number) => {
    const res = await api.get(`/monitoring/tindak-lanjut/${rekomendasiId}`);
    return res.data.data as PpeppRekomendasi;
  },

  // Laporkan hambatan
  reportHambatan: async (rekomendasiId: number, data: { jenis_hambatan: string; deskripsi: string; usulan_solusi?: string }) => {
    const res = await api.post(`/monitoring/tindak-lanjut/${rekomendasiId}/hambatan`, data);
    return res.data;
  },

  // Perpanjang deadline
  perpanjangDeadline: async (rekomendasiId: number, target_date: string, alasan: string) => {
    const res = await api.post(`/monitoring/tindak-lanjut/${rekomendasiId}/perpanjang-deadline`, { target_date, alasan });
    return res.data;
  },

  // Ganti PIC
  gantiPic: async (rekomendasiId: number, pic_user_id: number, alasan: string) => {
    const res = await api.post(`/monitoring/tindak-lanjut/${rekomendasiId}/ganti-pic`, { pic_user_id, alasan });
    return res.data;
  },

  // Kembalikan ke RAPIM
  kembalikanKeRapim: async (rekomendasiId: number, alasan: string) => {
    const res = await api.post(`/monitoring/tindak-lanjut/${rekomendasiId}/kembalikan-rapim`, { alasan });
    return res.data;
  },

  // Kandidat PIC
  getPicCandidates: async (unitId?: number) => {
    const res = await api.get('/monitoring/pic-candidates', { params: unitId ? { unit_id: unitId } : {} });
    return res.data.data as { id: number; name: string; email: string; unit_id: number; unit?: { id: number; name: string } }[];
  },

  // Reminders (H-7, H-3, H-1, overdue)
  getReminders: async () => {
    const res = await api.get('/monitoring/reminders');
    return res.data.data as ReminderData[];
  },

  // Riwayat pengiriman pengingat
  getReminderHistory: async (filters?: {
    laporan_id?: number;
    pengirim_id?: number;
    penerima_id?: number;
    status?: 'berhasil' | 'gagal' | 'dilewati';
    kanal?: 'aplikasi' | 'email';
    page?: number;
    per_page?: number;
  }) => {
    const res = await api.get('/monitoring/reminder-history', { params: filters });
    return res.data.data as PaginatedResponse<RiwayatPengingatItem>;
  },

  // Trigger eskalasi
  triggerEscalation: async (laporanId: number) => {
    const res = await api.post(`/monitoring/escalate/${laporanId}`);
    return res.data;
  },

  // Send reminder manually
  sendReminder: async (laporanId: number, message?: string) => {
    const res = await api.post(`/monitoring/remind/${laporanId}`, { message });
    return res.data;
  },

  // Unit performance comparison
  getUnitPerformance: async (filters?: { periode_id?: number }) => {
    const res = await api.get('/monitoring/unit-performance', { params: filters });
    return res.data.data as {
      unit: string;
      unit_id: number;
      total: number;
      selesai: number;
      overdue: number;
      avg_days: number;
      completion_rate: number;
    }[];
  },

  // Trend analysis
  getTrend: async (filters?: { tahun?: number; granularity?: 'harian' | 'mingguan' | 'bulanan' }) => {
    const res = await api.get('/monitoring/trend', { params: filters });
    return (res.data.data || res.data) as {
      label: string;
      laporan_masuk: number;
      laporan_selesai: number;
    }[];
  },

  // Risk distribution
  getRiskDistribution: async (filters?: { periode_id?: number; unit_id?: number }) => {
    const res = await api.get('/monitoring/risk-distribution', { params: filters });
    return (res.data.data || res.data) as Record<string, number>;
  },

  // Category distribution
  getCategoryDistribution: async (filters?: { periode_id?: number }) => {
    const res = await api.get('/monitoring/category-distribution', { params: filters });
    return (res.data.data || res.data) as { kategori: string; jumlah: number; color: string }[];
  },

  // SLA compliance
  getSlaCompliance: async (filters?: { periode_id?: number }) => {
    const res = await api.get('/monitoring/sla-compliance', { params: filters });
    return (res.data.data || res.data) as {
      total: number;
      on_time: number;
      late: number;
      compliance_rate: number;
    };
  },

  // Add progress with evidence
  addProgress: async (rekomendasiId: number, data: { progress_percentage: number; keterangan: string; bukti?: File }) => {
    const formData = new FormData();
    formData.append('progress_percentage', data.progress_percentage.toString());
    formData.append('keterangan', data.keterangan);
    if (data.bukti) {
      formData.append('bukti', data.bukti);
    }

    // Assuming the backend endpoint is POST /monitoring/tindak-lanjut/:id/progress
    const res = await api.post(`/monitoring/tindak-lanjut/${rekomendasiId}/progress`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return res.data;
  },

  // PIC ajukan konfirmasi selesai (status menunggu_konfirmasi)
  ajukanKonfirmasiSelesai: async (rekomendasiId: number) => {
    const res = await api.post(`/monitoring/tindak-lanjut/${rekomendasiId}/ajukan-konfirmasi-selesai`);
    return res.data;
  },

  // Admin/Rektor tolak konfirmasi selesai (kembali ke berlangsung)
  tolakKonfirmasiSelesai: async (rekomendasiId: number, alasan: string) => {
    const res = await api.post(`/monitoring/tindak-lanjut/${rekomendasiId}/tolak-konfirmasi-selesai`, { alasan });
    return res.data;
  },

  // Export monitoring report
  exportReport: async (format: 'pdf' | 'excel', filters?: MonitoringFilter) => {
    try {
      const res = await api.get('/monitoring/export', {
        params: { format, ...filters },
        responseType: 'arraybuffer',
      });

      const contentType = res.headers['content-type'];
      if (contentType && contentType.includes('application/json')) {
        const errorData = JSON.parse(new TextDecoder().decode(res.data));
        throw new Error(errorData.message || 'Export failed');
      }

      return res.data;
    } catch (error: any) {
      console.error('Export service error:', error);
      throw error;
    }
  },

  // Export specific Tindak Lanjut PDF
  exportTindakLanjutPDF: async (rekomendasiId: number) => {
    try {
      const res = await api.get(`/monitoring/tindak-lanjut/${rekomendasiId}/export/pdf`, {
        responseType: 'arraybuffer',
      });

      const contentType = res.headers['content-type'];
      if (contentType && contentType.includes('application/json')) {
        const errorData = JSON.parse(new TextDecoder().decode(res.data));
        throw new Error(errorData.message || 'Export failed');
      }

      return res.data;
    } catch (error: any) {
      console.error('Export service error:', error);
      throw error;
    }
  },

  // === KPI ===

  getKpiDashboard: async (filters?: MonitoringFilter) => {
    const res = await api.get('/monitoring/kpi-dashboard', { params: filters });
    return res.data;
  },

  createKpi: async (rekomendasiId: number, data: {
    nama_kpi: string;
    target_value: number;
    unit_of_measure: string;
    measurement_method?: string;
    due_date?: string;
  }) => {
    const res = await api.post(`/ppepp/${rekomendasiId}/kpi`, data);
    return res.data;
  },

  updateKpi: async (rekomendasiId: number, kpiId: number, data: any) => {
    const res = await api.put(`/ppepp/${rekomendasiId}/kpi/${kpiId}`, data);
    return res.data;
  },
};


